<script setup>
/**
 * Agent bots.
 *
 * Archetype E. The list, the create/edit dialog and the delete confirmation are
 * unchanged; what moves is the chrome - the title and `Add bot` go to the
 * shell's fixed header, search sits in the toolbar row above the table.
 */
import { ref, computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import SettingsSection from 'dashboard/components-next/Settings/SettingsSection.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsSearchInput from 'dashboard/components-next/Settings/SettingsSearchInput.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AgentBotModal from './components/AgentBotModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const store = useStore();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');
const selectedBot = ref({});
const searchQuery = ref('');
const loading = ref({});
const modalType = ref(MODAL_TYPES.CREATE);
const agentBotModalRef = ref(null);
const agentBotDeleteDialogRef = ref(null);

const tableHeaders = computed(() => {
  return [
    t('AGENT_BOTS.LIST.TABLE_HEADER.DETAILS'),
    t('AGENT_BOTS.LIST.TABLE_HEADER.URL'),
    t('AGENT_BOTS.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const selectedBotName = computed(() => selectedBot.value?.name || '');

const filteredAgentBots = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentBots.value;
  return picoSearch(agentBots.value, query, ['name', 'description']);
});

const openAddModal = () => {
  modalType.value = MODAL_TYPES.CREATE;
  selectedBot.value = {};
  agentBotModalRef.value.dialogRef.open();
};

const openEditModal = bot => {
  modalType.value = MODAL_TYPES.EDIT;
  selectedBot.value = bot;
  agentBotModalRef.value.dialogRef.open();
};

const openDeletePopup = bot => {
  selectedBot.value = bot;
  agentBotDeleteDialogRef.value.open();
};

const deleteAgentBot = async id => {
  try {
    await store.dispatch('agentBots/delete', id);
    useAlert(t('AGENT_BOTS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_BOTS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
    selectedBot.value = {};
  }
};

const confirmDeletion = () => {
  loading.value[selectedBot.value.id] = true;
  deleteAgentBot(selectedBot.value.id);
  agentBotDeleteDialogRef.value.close();
};

onMounted(() => {
  store.dispatch('agentBots/get');
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('AGENT_BOTS.HEADER')"
    :description="$t('AGENT_BOTS.DESCRIPTION')"
  >
    <template #actions>
      <Button
        size="sm"
        :label="$t('AGENT_BOTS.ADD.TITLE')"
        @click="openAddModal"
      />
    </template>

    <template #tabs>
      <SettingsSearchInput
        v-model="searchQuery"
        :placeholder="$t('AGENT_BOTS.SEARCH_PLACEHOLDER')"
      />
    </template>

    <SettingsSection
      v-if="uiFlags.isFetching"
      role="status"
      aria-busy="true"
      :aria-label="$t('AGENT_BOTS.LIST.LOADING')"
    >
      <div
        v-for="row in 4"
        :key="row"
        class="flex items-center gap-3 px-4 py-4"
      >
        <Skeleton variant="circle" :announce="false" class="size-8" />
        <Skeleton variant="text" :lines="2" :announce="false" class="flex-1" />
      </div>
    </SettingsSection>

    <BaseTable
      v-else
      :headers="tableHeaders"
      :items="filteredAgentBots"
      :no-data-message="
        searchQuery ? $t('AGENT_BOTS.NO_RESULTS') : $t('AGENT_BOTS.LIST.404')
      "
    >
      <template #row="{ items }">
        <BaseTableRow v-for="bot in items" :key="bot.id" :item="bot">
          <template #default>
            <BaseTableCell class="max-w-0">
              <div class="flex items-center min-w-0 gap-4">
                <Avatar
                  :name="bot.name || ''"
                  :src="bot.thumbnail"
                  :size="40"
                  class="flex-shrink-0"
                />
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="truncate text-body-main text-n-slate-12">
                      {{ bot.name }}
                    </span>
                    <span
                      v-if="bot.system_bot"
                      class="text-xs text-n-slate-12 bg-n-violet-5 rounded-md py-0.5 px-1 flex-shrink-0"
                    >
                      {{ $t('AGENT_BOTS.GLOBAL_BOT_BADGE') }}
                    </span>
                  </div>
                  <span class="block truncate text-body-main text-n-slate-11">
                    {{ bot.description }}
                  </span>
                </div>
              </div>
            </BaseTableCell>

            <BaseTableCell class="max-w-0">
              <span class="block truncate text-body-main text-n-slate-11">
                {{ bot.outgoing_url || bot.bot_config?.webhook_url }}
              </span>
            </BaseTableCell>

            <BaseTableCell align="end" class="w-24">
              <div class="flex justify-end flex-shrink-0 gap-3">
                <Button
                  v-if="!bot.system_bot"
                  v-tooltip.top="$t('AGENT_BOTS.EDIT.BUTTON_TEXT')"
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  :is-loading="loading[bot.id]"
                  @click="openEditModal(bot)"
                />
                <Button
                  v-if="!bot.system_bot"
                  v-tooltip.top="$t('AGENT_BOTS.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[bot.id]"
                  @click="openDeletePopup(bot)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <AgentBotModal
      ref="agentBotModalRef"
      :type="modalType"
      :selected-bot="selectedBot"
    />

    <Dialog
      ref="agentBotDeleteDialogRef"
      type="alert"
      :title="$t('AGENT_BOTS.DELETE.CONFIRM.TITLE')"
      :description="
        $t('AGENT_BOTS.DELETE.CONFIRM.MESSAGE', { name: selectedBotName })
      "
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="$t('AGENT_BOTS.DELETE.CONFIRM.YES')"
      :cancel-button-label="$t('AGENT_BOTS.DELETE.CONFIRM.NO')"
      @confirm="confirmDeletion"
    />
  </SettingsPageShell>
</template>
